import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    TrendingUp,
    TrendingDown,
    Users,
    Activity,
    Smartphone,
    Monitor,
    Clock,
    BarChart3,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    Camera,
    X,
    RefreshCw,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import { useAdmin } from '../contexts/AdminContext';
import {
    getOverview,
    getRetention,
    getScreenStats,
    getFeatureStats,
    getRealTimeStats,
    getSnapshotMetrics,
    getAnalyticsSummary,
    resetAnalytics
} from '../utils/analyticsApi';
import { formatNumber } from '../utils/helpers';
import toast from 'react-hot-toast';

// Period options for date range selector
const PERIOD_OPTIONS = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
];

// Colors for charts
const CHART_COLORS = {
    primary: '#3B82F6',
    secondary: '#10B981',
    tertiary: '#F59E0B',
    quaternary: '#EF4444',
    ios: '#007AFF',
    android: '#3DDC84',
    d1: '#3B82F6',
    d7: '#10B981',
    d30: '#F59E0B'
};

// KPI Card Component
function KPICard({ title, value, change, changeLabel, icon: Icon, color = 'blue' }) {
    const colorClasses = {
        blue: 'bg-blue/10 text-blue',
        green: 'bg-green/10 text-green',
        purple: 'bg-purple/10 text-purple',
        orange: 'bg-orange/10 text-orange'
    };

    const isPositive = change > 0;
    const isNeutral = change === 0 || change === undefined;

    return (
        <Card className="relative overflow-hidden">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                        {title}
                    </p>
                    <p className="text-3xl font-bold">
                        {formatNumber(value)}
                    </p>
                    {change !== undefined && (
                        <div className={`flex items-center mt-2 text-sm ${isNeutral ? 'text-muted-light dark:text-muted-dark' :
                            isPositive ? 'text-green' : 'text-red'
                            }`}>
                            {isNeutral ? (
                                <Minus size={16} className="mr-1" />
                            ) : isPositive ? (
                                <ArrowUpRight size={16} className="mr-1" />
                            ) : (
                                <ArrowDownRight size={16} className="mr-1" />
                            )}
                            <span>{isNeutral ? '0' : Math.abs(change).toFixed(1)}%</span>
                            {changeLabel && (
                                <span className="ml-1 text-muted-light dark:text-muted-dark">
                                    {changeLabel}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
                    <Icon size={24} />
                </div>
            </div>
        </Card>
    );
}

// Platform distribution chart
function PlatformChart({ data }) {
    if (!data || (data.ios === 0 && data.android === 0)) {
        return (
            <div className="flex items-center justify-center h-48 text-muted-light dark:text-muted-dark">
                No platform data available
            </div>
        );
    }

    const chartData = [
        { name: 'iOS', value: data.ios, color: CHART_COLORS.ios },
        { name: 'Android', value: data.android, color: CHART_COLORS.android }
    ];

    const total = data.ios + data.android;

    return (
        <div className="flex items-center">
            <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                    <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value) => [formatNumber(value), 'Sessions']}
                    />
                </PieChart>
            </ResponsiveContainer>
            <div className="space-y-4">
                {chartData.map((item) => (
                    <div key={item.name} className="flex items-center gap-3">
                        <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: item.color }}
                        />
                        <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                {formatNumber(item.value)} ({((item.value / total) * 100).toFixed(1)}%)
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Retention cohort table
function RetentionTable({ cohorts }) {
    if (!cohorts || cohorts.length === 0) {
        return (
            <div className="flex items-center justify-center h-48 text-muted-light dark:text-muted-dark">
                No retention data available
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border-light dark:border-border-dark">
                        <th className="text-left py-3 px-4 font-medium">Cohort</th>
                        <th className="text-right py-3 px-4 font-medium">Size</th>
                        <th className="text-right py-3 px-4 font-medium">D1</th>
                        <th className="text-right py-3 px-4 font-medium">D7</th>
                        <th className="text-right py-3 px-4 font-medium">D30</th>
                    </tr>
                </thead>
                <tbody>
                    {cohorts.map((cohort) => (
                        <tr
                            key={cohort.cohortDate}
                            className="border-b border-border-light dark:border-border-dark hover:bg-accent-light dark:hover:bg-accent-dark"
                        >
                            <td className="py-3 px-4">{cohort.cohortDate}</td>
                            <td className="py-3 px-4 text-right">{formatNumber(cohort.cohortSize)}</td>
                            <td className="py-3 px-4 text-right">
                                <RetentionCell value={cohort.d1Retention} />
                            </td>
                            <td className="py-3 px-4 text-right">
                                <RetentionCell value={cohort.d7Retention} />
                            </td>
                            <td className="py-3 px-4 text-right">
                                <RetentionCell value={cohort.d30Retention} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function RetentionCell({ value }) {
    const getColor = (val) => {
        if (val >= 40) return 'bg-green/20 text-green';
        if (val >= 20) return 'bg-yellow-500/20 text-yellow-600';
        if (val > 0) return 'bg-orange/20 text-orange';
        return 'text-muted-light dark:text-muted-dark';
    };

    return (
        <span className={`px-2 py-1 rounded ${getColor(value)}`}>
            {value > 0 ? `${value.toFixed(1)}%` : '-'}
        </span>
    );
}

// Snapshot Modal Component
function SnapshotModal({ isOpen, onClose, data, isLoading }) {
    if (!isOpen) return null;

    return (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" style={{ margin: 0, transform: 'none' }}>
            <div className="bg-background-light dark:bg-background-dark rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border-dark">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <Camera size={24} className="text-purple" />
                            Live Snapshot
                        </h2>
                        {data?.snapshotTime && (
                            <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                Calculated at {new Date(data.snapshotTime).toLocaleString()}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-accent-light dark:hover:bg-accent-dark transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-4">
                            <LoadingSpinner size="large" />
                            <p className="text-muted-light dark:text-muted-dark">
                                Calculating metrics from raw events...
                            </p>
                        </div>
                    ) : data && (data.metrics || data.snapshotTime) ? (
                        <div className="space-y-6">
                            {/* Key Metrics */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Key Metrics (Last {data.periodDays} Days)</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                        <p className="text-sm text-muted-light dark:text-muted-dark">DAU (Today)</p>
                                        <p className="text-2xl font-bold text-blue">{formatNumber(data.metrics?.dau || 0)}</p>
                                    </div>
                                    <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                        <p className="text-sm text-muted-light dark:text-muted-dark">WAU (7 Days)</p>
                                        <p className="text-2xl font-bold text-green">{formatNumber(data.metrics?.wau || 0)}</p>
                                    </div>
                                    <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                        <p className="text-sm text-muted-light dark:text-muted-dark">MAU (30 Days)</p>
                                        <p className="text-2xl font-bold text-purple">{formatNumber(data.metrics?.mau || 0)}</p>
                                    </div>
                                    <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                        <p className="text-sm text-muted-light dark:text-muted-dark">New Users Today</p>
                                        <p className="text-2xl font-bold text-orange">{formatNumber(data.metrics?.newUsersToday || 0)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                    <p className="text-sm text-muted-light dark:text-muted-dark">New Users (Period)</p>
                                    <p className="text-xl font-semibold">{formatNumber(data.metrics?.newUsersInPeriod || 0)}</p>
                                </div>
                                <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                    <p className="text-sm text-muted-light dark:text-muted-dark">Total Sessions</p>
                                    <p className="text-xl font-semibold">{formatNumber(data.metrics?.totalSessions || 0)}</p>
                                </div>
                                <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                    <p className="text-sm text-muted-light dark:text-muted-dark">Total Events</p>
                                    <p className="text-xl font-semibold">{formatNumber(data.metrics?.totalEvents || 0)}</p>
                                </div>
                                <div className="bg-accent-light dark:bg-accent-dark rounded-xl p-4">
                                    <p className="text-sm text-muted-light dark:text-muted-dark">Avg Session Duration</p>
                                    <p className="text-xl font-semibold">{formatDuration(data.metrics?.avgSessionDurationMs || 0)}</p>
                                </div>
                            </div>

                            {/* Daily Chart */}
                            {data.dailyData && data.dailyData.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Daily Breakdown</h3>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <LineChart data={data.dailyData}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border-light dark:stroke-border-dark" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 10 }}
                                                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip
                                                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                                                formatter={(value, name) => [formatNumber(value), name === 'dau' ? 'DAU' : name === 'newUsers' ? 'New Users' : name]}
                                            />
                                            <Line type="monotone" dataKey="dau" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} name="DAU" />
                                            <Line type="monotone" dataKey="newUsers" stroke={CHART_COLORS.secondary} strokeWidth={2} dot={false} name="New Users" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Top Screens & Features */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Top Screens */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Top Screens</h3>
                                    {data.topScreens && data.topScreens.length > 0 ? (
                                        <div className="space-y-2">
                                            {data.topScreens.slice(0, 10).map((item, idx) => (
                                                <div key={item.screen} className="flex items-center justify-between">
                                                    <span className="text-sm truncate flex-1">
                                                        <span className="text-muted-light dark:text-muted-dark mr-2">{idx + 1}.</span>
                                                        {item.screen}
                                                    </span>
                                                    <span className="text-sm font-medium ml-2">{formatNumber(item.count)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted-light dark:text-muted-dark text-sm">No screen data</p>
                                    )}
                                </div>

                                {/* Top Features */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Top Features</h3>
                                    {data.topFeatures && data.topFeatures.length > 0 ? (
                                        <div className="space-y-2">
                                            {data.topFeatures.slice(0, 10).map((item, idx) => (
                                                <div key={item.feature} className="flex items-center justify-between">
                                                    <span className="text-sm truncate flex-1">
                                                        <span className="text-muted-light dark:text-muted-dark mr-2">{idx + 1}.</span>
                                                        {item.feature}
                                                    </span>
                                                    <span className="text-sm font-medium ml-2">{formatNumber(item.count)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted-light dark:text-muted-dark text-sm">No feature data</p>
                                    )}
                                </div>
                            </div>

                            {/* Platform & Version */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Platform */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Platform Distribution</h3>
                                    <div className="flex gap-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.ios }} />
                                            <span className="text-sm">iOS: {formatNumber(data.platformBreakdown?.ios || 0)}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS.android }} />
                                            <span className="text-sm">Android: {formatNumber(data.platformBreakdown?.android || 0)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Version */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Version Distribution</h3>
                                    {data.versionBreakdown && Object.keys(data.versionBreakdown).length > 0 ? (
                                        <div className="space-y-1">
                                            {Object.entries(data.versionBreakdown)
                                                .sort((a, b) => b[1] - a[1])
                                                .slice(0, 5)
                                                .map(([version, count]) => (
                                                    <div key={version} className="flex items-center justify-between text-sm">
                                                        <span className="font-mono">{version}</span>
                                                        <span>{formatNumber(count)}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted-light dark:text-muted-dark text-sm">No version data</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-64 text-muted-light dark:text-muted-dark gap-4">
                            <p>No snapshot data available</p>
                            <p className="text-sm text-center max-w-md">
                                This could mean: no events recorded yet, or the analytics Convex deployment needs to be updated.
                                Check browser console for errors.
                            </p>
                            {data && (
                                <pre className="text-xs bg-accent-light dark:bg-accent-dark p-2 rounded max-w-md overflow-auto">
                                    {JSON.stringify(data, null, 2)}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Version distribution table
function VersionTable({ versions }) {
    if (!versions || Object.keys(versions).length === 0) {
        return (
            <div className="flex items-center justify-center h-32 text-muted-light dark:text-muted-dark">
                No version data available
            </div>
        );
    }

    const sortedVersions = Object.entries(versions)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

    const total = sortedVersions.reduce((sum, [, count]) => sum + count, 0);

    return (
        <div className="space-y-2">
            {sortedVersions.map(([version, count]) => (
                <div key={version} className="flex items-center justify-between">
                    <span className="font-mono text-sm">{version}</span>
                    <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-accent-light dark:bg-accent-dark rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue rounded-full"
                                style={{ width: `${(count / total) * 100}%` }}
                            />
                        </div>
                        <span className="text-sm text-muted-light dark:text-muted-dark w-12 text-right">
                            {formatNumber(count)}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function Analytics() {
    const [period, setPeriod] = useState('7d');
    const [showSnapshot, setShowSnapshot] = useState(false);
    const [snapshotData, setSnapshotData] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetConfirmation, setResetConfirmation] = useState('');

    const { admin } = useAdmin();
    const queryClient = useQueryClient();
    const isSuperAdmin = admin?.privileges?.includes('super_admin');

    // Snapshot mutation (on-demand calculation)
    const snapshotMutation = useMutation({
        mutationFn: () => getSnapshotMetrics({ periodDays: 7 }),
        onSuccess: (response) => {
            console.log('Snapshot response:', response);
            // Response is already unwrapped by axios interceptor: { success: true, data: {...} }
            setSnapshotData(response?.data || response);
            setShowSnapshot(true);
        },
        onError: (error) => {
            console.error('Snapshot error:', error);
            setShowSnapshot(true); // Still show modal to display error state
        }
    });

    // Reset analytics mutation
    const resetMutation = useMutation({
        mutationFn: (confirmation) => resetAnalytics(confirmation),
        onSuccess: (response) => {
            toast.success('All analytics data has been reset');
            setShowResetModal(false);
            setResetConfirmation('');
            // Invalidate all analytics queries
            queryClient.invalidateQueries({ queryKey: ['analytics-overview'] });
            queryClient.invalidateQueries({ queryKey: ['analytics-retention'] });
            queryClient.invalidateQueries({ queryKey: ['analytics-screens'] });
            queryClient.invalidateQueries({ queryKey: ['analytics-features'] });
            queryClient.invalidateQueries({ queryKey: ['analytics-realtime'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || error.message || 'Failed to reset analytics');
        }
    });

    // Analytics summary query (for reset modal)
    const { data: summaryData } = useQuery({
        queryKey: ['analytics-summary'],
        queryFn: getAnalyticsSummary,
        enabled: showResetModal,
        staleTime: 0 // Always fetch fresh
    });
    const summary = summaryData?.data;

    const handleSnapshot = () => {
        setShowSnapshot(true);
        snapshotMutation.mutate();
    };

    const handleResetAnalytics = () => {
        if (resetConfirmation === 'i confirm') {
            resetMutation.mutate(resetConfirmation);
        }
    };

    const openResetModal = () => {
        setResetConfirmation('');
        setShowResetModal(true);
    };

    const closeResetModal = () => {
        setShowResetModal(false);
        setResetConfirmation('');
    };

    // Fetch overview metrics
    const { data: overviewData, isLoading: loadingOverview } = useQuery({
        queryKey: ['analytics-overview', period],
        queryFn: () => getOverview({ period }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchInterval: 5 * 60 * 1000
    });

    // Fetch retention data
    const { data: retentionData, isLoading: loadingRetention } = useQuery({
        queryKey: ['analytics-retention'],
        queryFn: () => getRetention({ days: 30 }),
        staleTime: 30 * 60 * 1000 // 30 minutes
    });

    // Fetch screen stats
    const { data: screenData, isLoading: loadingScreens } = useQuery({
        queryKey: ['analytics-screens', period],
        queryFn: () => getScreenStats({ period, limit: 10 }),
        staleTime: 5 * 60 * 1000
    });

    // Fetch feature stats
    const { data: featureData, isLoading: loadingFeatures } = useQuery({
        queryKey: ['analytics-features', period],
        queryFn: () => getFeatureStats({ period }),
        staleTime: 5 * 60 * 1000
    });

    // Fetch real-time stats
    const { data: realtimeData } = useQuery({
        queryKey: ['analytics-realtime'],
        queryFn: getRealTimeStats,
        refetchInterval: 30 * 1000 // 30 seconds
    });

    const overview = overviewData?.data;
    const changes = overviewData?.data?.changes || {};
    const retention = retentionData?.data || [];
    const screens = screenData?.data || [];
    const features = featureData?.data || [];
    const realtime = realtimeData?.data;

    if (loadingOverview && !overview) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with period selector */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Analytics</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Platform metrics and user insights
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Reset Analytics Button - Super Admin Only */}
                    {isSuperAdmin && (
                        <>
                            <button
                                onClick={openResetModal}
                                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-red-500/10 text-red-500 hover:bg-red-500/20 flex items-center gap-2"
                            >
                                <Trash2 size={16} />
                                Reset Analytics
                            </button>
                            <div className="w-px h-6 bg-border-light dark:bg-border-dark" />
                        </>
                    )}
                    {/* Snapshot Button */}
                    <button
                        onClick={handleSnapshot}
                        disabled={snapshotMutation.isPending}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-purple/10 text-purple hover:bg-purple/20 flex items-center gap-2 disabled:opacity-50"
                    >
                        {snapshotMutation.isPending ? (
                            <RefreshCw size={16} className="animate-spin" />
                        ) : (
                            <Camera size={16} />
                        )}
                        Live Snapshot
                    </button>
                    <div className="w-px h-6 bg-border-light dark:bg-border-dark" />
                    {PERIOD_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => setPeriod(option.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${period === option.value
                                ? 'bg-foreground-light dark:bg-foreground-dark text-background-light dark:text-background-dark'
                                : 'bg-accent-light dark:bg-accent-dark hover:bg-border-light dark:hover:bg-border-dark'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Real-time indicator */}
            {realtime && (
                <Card className="bg-green/5 border-green/20">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-green"></span>
                            </span>
                            <span className="font-medium text-green">Live</span>
                        </div>
                        <div className="text-sm">
                            <span className="font-semibold">{realtime.activeSessions || 0}</span>
                            {' '}active sessions • {' '}
                            <span className="font-semibold">{realtime.eventsLastHour || 0}</span>
                            {' '}events in the last hour
                        </div>
                    </div>
                </Card>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard
                    title="Daily Active Users"
                    value={overview?.currentPeriod?.dau || 0}
                    change={changes.dauChange}
                    changeLabel="vs prev period"
                    icon={Users}
                    color="blue"
                />
                <KPICard
                    title="Weekly Active Users"
                    value={overview?.currentPeriod?.wau || 0}
                    change={changes.wauChange}
                    changeLabel="vs prev period"
                    icon={Activity}
                    color="green"
                />
                <KPICard
                    title="Monthly Active Users"
                    value={overview?.currentPeriod?.mau || 0}
                    change={changes.mauChange}
                    changeLabel="vs prev period"
                    icon={TrendingUp}
                    color="purple"
                />
                <KPICard
                    title="New Users"
                    value={overview?.currentPeriod?.newUsers || 0}
                    change={changes.newUsersChange}
                    changeLabel="vs prev period"
                    icon={Users}
                    color="orange"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* DAU Over Time */}
                <Card title="Daily Active Users Over Time">
                    {overview?.dailyData && overview.dailyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={overview.dailyData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border-light dark:stroke-border-dark" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip
                                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                                    formatter={(value) => [formatNumber(value), 'DAU']}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="dau"
                                    stroke={CHART_COLORS.primary}
                                    strokeWidth={2}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-muted-light dark:text-muted-dark">
                            No daily data available
                        </div>
                    )}
                </Card>

                {/* New Users Over Time */}
                <Card title="New Users Per Day">
                    {overview?.dailyData && overview.dailyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={overview.dailyData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border-light dark:stroke-border-dark" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12 }}
                                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip
                                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                                    formatter={(value) => [formatNumber(value), 'New Users']}
                                />
                                <Bar dataKey="newUsers" fill={CHART_COLORS.secondary} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-muted-light dark:text-muted-dark">
                            No daily data available
                        </div>
                    )}
                </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Screens */}
                <Card title="Top Screens">
                    {loadingScreens ? (
                        <div className="flex items-center justify-center h-[300px]">
                            <LoadingSpinner />
                        </div>
                    ) : screens.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={screens} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border-light dark:stroke-border-dark" />
                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                <YAxis
                                    type="category"
                                    dataKey="screen"
                                    tick={{ fontSize: 12 }}
                                    width={100}
                                />
                                <Tooltip formatter={(value) => [formatNumber(value), 'Views']} />
                                <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-muted-light dark:text-muted-dark">
                            No screen data available
                        </div>
                    )}
                </Card>

                {/* Feature Usage */}
                <Card title="Feature Usage">
                    {loadingFeatures ? (
                        <div className="flex items-center justify-center h-[300px]">
                            <LoadingSpinner />
                        </div>
                    ) : features.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={features.slice(0, 10)} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border-light dark:stroke-border-dark" />
                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                <YAxis
                                    type="category"
                                    dataKey="feature"
                                    tick={{ fontSize: 12 }}
                                    width={120}
                                />
                                <Tooltip
                                    formatter={(value, name) => {
                                        if (name === 'count') return [formatNumber(value), 'Uses'];
                                        if (name === 'adoptionPercent') return [`${value}%`, 'Adoption'];
                                        return [value, name];
                                    }}
                                />
                                <Bar dataKey="count" fill={CHART_COLORS.secondary} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-muted-light dark:text-muted-dark">
                            No feature data available
                        </div>
                    )}
                </Card>
            </div>

            {/* Charts Row 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Platform Distribution */}
                <Card title="Platform Distribution">
                    <PlatformChart data={overview?.platformBreakdown} />
                </Card>

                {/* App Version Distribution */}
                <Card title="App Version Distribution">
                    <VersionTable versions={overview?.versionBreakdown} />
                </Card>
            </div>

            {/* Retention Cohorts */}
            <Card title="Retention Cohorts">
                {loadingRetention ? (
                    <div className="flex items-center justify-center h-48">
                        <LoadingSpinner />
                    </div>
                ) : (
                    <RetentionTable cohorts={retention} />
                )}
            </Card>

            {/* Session Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-blue/10">
                            <BarChart3 size={24} className="text-blue" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                Total Sessions
                            </p>
                            <p className="text-2xl font-bold">
                                {formatNumber(overview?.currentPeriod?.totalSessions || 0)}
                            </p>
                        </div>
                    </div>
                </Card>
                <Card>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-green/10">
                            <Clock size={24} className="text-green" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                Avg Session Duration
                            </p>
                            <p className="text-2xl font-bold">
                                {formatDuration(overview?.currentPeriod?.avgSessionDurationMs || 0)}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Snapshot Modal */}
            <SnapshotModal
                isOpen={showSnapshot}
                onClose={() => setShowSnapshot(false)}
                data={snapshotData}
                isLoading={snapshotMutation.isPending}
            />

            {/* Reset Analytics Modal - Super Admin Only */}
            <Modal
                isOpen={showResetModal}
                onClose={closeResetModal}
                title="⚠️ Reset All Analytics"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={closeResetModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleResetAnalytics}
                            loading={resetMutation.isPending}
                            disabled={resetConfirmation !== 'i confirm'}
                            className="!bg-red-600 hover:!bg-red-700"
                        >
                            <Trash2 size={18} />
                            Reset All Analytics
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    {/* Critical Warning */}
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={24} />
                            <div>
                                <p className="font-bold text-red-500 text-lg">
                                    CRITICAL: IRREVERSIBLE ACTION
                                </p>
                                <p className="text-sm text-red-400 mt-1">
                                    This will <strong>permanently delete</strong> all analytics data. This action cannot be undone.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* What will be deleted */}
                    {summary && (
                        <div className="p-4 bg-accent-light dark:bg-accent-dark rounded-lg">
                            <p className="font-medium mb-3">Data that will be permanently removed:</p>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-light dark:text-muted-dark">Events:</span>
                                    <span className="font-medium">{formatNumber(summary.events || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-light dark:text-muted-dark">Sessions:</span>
                                    <span className="font-medium">{formatNumber(summary.sessions || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-light dark:text-muted-dark">Daily Metrics:</span>
                                    <span className="font-medium">{formatNumber(summary.dailyMetrics || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-light dark:text-muted-dark">Retention Cohorts:</span>
                                    <span className="font-medium">{formatNumber(summary.retentionCohorts || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-light dark:text-muted-dark">User First Seen:</span>
                                    <span className="font-medium">{formatNumber(summary.userFirstSeen || 0)}</span>
                                </div>
                                <div className="flex justify-between font-semibold border-t border-border-light dark:border-border-dark pt-2 mt-2">
                                    <span>Total Records:</span>
                                    <span className="text-red-500">{formatNumber(summary.total || 0)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Confirmation Input */}
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Type <strong className="text-red-500">"i confirm"</strong> to proceed:
                        </label>
                        <Input
                            value={resetConfirmation}
                            onChange={(e) => setResetConfirmation(e.target.value.toLowerCase())}
                            placeholder="i confirm"
                            className={resetConfirmation && resetConfirmation !== 'i confirm' ? 'border-red' : ''}
                        />
                        {resetConfirmation && resetConfirmation !== 'i confirm' && (
                            <p className="text-sm text-red mt-1">
                                Type "i confirm" exactly to proceed
                            </p>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
}

// Helper function to format duration
function formatDuration(ms) {
    if (!ms || ms === 0) return '0s';

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
}
